import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Collapse,
  Chip,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import SettingsIcon from '@mui/icons-material/Settings';
import ConstructionIcon from '@mui/icons-material/Construction';
import PanToolIcon from '@mui/icons-material/PanTool';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import OpacityIcon from '@mui/icons-material/Opacity';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import { InspectionItemRow } from './InspectionItemRow';
import type { InspectionCategory as InspectionCategoryType, InspectionItemCondition, MeasurementValueEntry } from '@/types';
import type { RecommendationUrgency } from '@/hooks/useInspections';

interface InspectionCategoryProps {
  category: InspectionCategoryType;
  editable?: boolean;
  defaultExpanded?: boolean;
  onConditionChange?: (itemId: string, condition: InspectionItemCondition) => void;
  onNotesChange?: (itemId: string, notes: string) => void | Promise<void>;
  onMeasurementValuesChange?: (itemId: string, values: MeasurementValueEntry[]) => void;
  onMeasurementChange?: (itemId: string, value: number | null) => void;
  onAddPhoto?: (itemId: string, file: File) => Promise<void>;
  onDeletePhoto?: (itemId: string, photoId: string) => Promise<void>;
  onAddRecommendation?: (
    itemId: string,
    recommendation: {
      description: string;
      customerDescription?: string;
      urgency: RecommendationUrgency;
      partsCostCents: number;
      laborCostCents: number;
      laborHours?: number;
    }
  ) => Promise<void>;
  isUploadingPhoto?: boolean;
  isSavingItem?: boolean;
  isAddingRecommendation?: boolean;
  vehicleMake?: string;
  vehicleModel?: string;
}

const iconMap: Record<string, typeof DirectionsCarIcon> = {
  DirectionsCar: DirectionsCarIcon,
  AirlineSeatReclineNormal: AirlineSeatReclineNormalIcon,
  Settings: SettingsIcon,
  Construction: ConstructionIcon,
  PanTool: PanToolIcon,
  TireRepair: TireRepairIcon,
  Opacity: OpacityIcon,
  ElectricalServices: ElectricalServicesIcon,
};

export function InspectionCategoryComponent({
  category,
  editable = false,
  defaultExpanded = true,
  onConditionChange,
  onNotesChange,
  onMeasurementChange,
  onMeasurementValuesChange,
  onAddPhoto,
  onDeletePhoto,
  onAddRecommendation,
  isUploadingPhoto,
  isSavingItem = false,
  isAddingRecommendation,
  vehicleMake,
  vehicleModel,
}: InspectionCategoryProps) {
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Calculate statistics
  const totalItems = category.items.length;
  const inspectedItems = category.items.filter((item) => item.condition !== null).length;
  const issueItems = category.items.filter(
    (item) => item.condition === 'poor' || item.condition === 'critical'
  ).length;
  const progress = totalItems > 0 ? (inspectedItems / totalItems) * 100 : 0;

  const Icon = category.icon ? iconMap[category.icon] || DirectionsCarIcon : DirectionsCarIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: issueItems > 0 ? 'error.main' : 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: 'pointer',
          backgroundColor: issueItems > 0 ? 'error.50' : 'transparent',
          '&:hover': {
            backgroundColor: issueItems > 0 ? 'error.100' : 'action.hover',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Icon */}
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            backgroundColor: issueItems > 0 ? 'error.main' : 'primary.main',
            color: 'white',
          }}
        >
          <Icon />
        </Box>

        {/* Title */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {category.name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                width: 100,
                height: 6,
                borderRadius: 3,
                backgroundColor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progress === 100 ? 'success.main' : 'primary.main',
                },
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {inspectedItems}/{totalItems}
            </Typography>
          </Box>
        </Box>

        {/* Issue Badge */}
        {issueItems > 0 && (
          <Chip
            size="small"
            color="error"
            label={t('issueCount', { count: issueItems })}
          />
        )}

        {/* Expand Button */}
        <Tooltip title={expanded ? tc('collapse') : tc('expand')}>
          <IconButton size="small" aria-label={expanded ? tc('collapse') : tc('expand')} sx={{ minWidth: 44, minHeight: 44 }}>
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Items */}
      <Collapse in={expanded}>
        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          {category.items.map((item) => (
            <InspectionItemRow
              key={item.id}
              item={item}
              editable={editable}
              onConditionChange={onConditionChange}
              onNotesChange={onNotesChange}
              onMeasurementChange={onMeasurementChange}
              onMeasurementValuesChange={onMeasurementValuesChange}
              onAddPhoto={onAddPhoto}
              onDeletePhoto={onDeletePhoto}
              onAddRecommendation={onAddRecommendation}
              isUploadingPhoto={isUploadingPhoto}
              isSavingItem={isSavingItem}
              isAddingRecommendation={isAddingRecommendation}
              vehicleMake={vehicleMake}
              vehicleModel={vehicleModel}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}
